/**
 * GlobalMenuModal Styles
 * Styles for the GlobalMenuModal component (gamified menu with centered avatar)
 */

import { StyleSheet, Platform } from 'react-native';
import { getModalContentAlignment, getDrawerWrapperPosition } from './helpers/getModalContentPosition';
import type { Theme } from '../../src/theme/getTheme';

/** Avatar size for the centered profile (used by component and styles) */
export const MENU_AVATAR_SIZE = 72;

/** Width of the right-side drawer as fraction of screen (0.75 = 70–80%) */
export const MENU_DRAWER_WIDTH_FRACTION = 0.75;

/** Max width in px so the drawer doesn't stretch too much on tablets/desktop */
export const MENU_DRAWER_MAX_WIDTH = 400;

/** Fallback opaque backgrounds so the menu is always visible even if theme is not ready (e.g. production) */
const MENU_CARD_BG_FALLBACK = '#FFFFFF';
const MENU_PRIMARY_BG_FALLBACK = '#F3EDFF';

/**
 * Get content position for GlobalMenuModal (full-screen wrapper for slide-in drawer)
 */
export const getGlobalMenuModalContentPosition = () => getDrawerWrapperPosition();

/**
 * Get content alignment for GlobalMenuModal
 * Uses shared helper for consistent alignment
 */
export const getGlobalMenuModalContentAlignment = () => getModalContentAlignment();

/** Insets for safe area (optional; when provided, applied to profile top and cancel footer bottom) */
export interface MenuModalInsets {
  top: number;
  bottom: number;
}

/**
 * Get gamified menu styles (theme-dependent)
 */
export const getGlobalMenuModalStyles = (
  theme: Theme,
  insets?: MenuModalInsets
) =>
  StyleSheet.create({
    drawerWrapper: {
      flex: 1,
      alignItems: 'flex-end',
      ...(Platform.OS === 'web' && {
        width: '100%' as never,
        height: '100%' as never,
        minHeight: '100%' as never,
      }),
    },
    menuContainer: {
      width: '75%',
      maxWidth: MENU_DRAWER_MAX_WIDTH,
      flex: 1,
      backgroundColor: theme.background?.card ?? MENU_CARD_BG_FALLBACK,
      ...(Platform.OS === 'web'
        ? {
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
            height: '100%' as never,
            minHeight: '100%' as never,
            zIndex: 1002,
          }
        : {
            shadowColor: theme.shadow,
            shadowOffset: { width: -2, height: 0 },
            shadowOpacity: 0.2,
            shadowRadius: 8,
            elevation: 8,
          }),
      overflow: 'hidden',
    },
    menuInner: {
      flex: 1,
      flexDirection: 'column',
      ...(Platform.OS === 'web' && { minHeight: '100%' as never }),
    },
    menuScroll: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
        overflowY: 'auto' as never,
        WebkitOverflowScrolling: 'touch' as never,
      }),
    },
    menuScrollContent: {
      flexGrow: 1,
      paddingBottom: 8,
    },
    profileSection: {
      backgroundColor: theme.background?.primary ?? MENU_PRIMARY_BG_FALLBACK,
      paddingBottom: 20,
      paddingHorizontal: 20,
      alignItems: 'center',
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: 48,
      paddingTop: 4 + (insets?.top ?? 0),
      paddingBottom: 8,
      paddingLeft: 12,
      paddingRight: 16,
      width: '100%',
    },
    themeToggleWrapper: {
      width: 80,
      height: 44,
      overflow: 'hidden',
      position: 'relative',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    themeToggleScaled: {
      position: 'absolute',
      left: -79,
      top: -41,
      width: 230,
      height: 120,
      transform: [{ scale: 0.32 }],
    },
    languageButton: {
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      width: 80,
      height: 44,
      borderRadius: 8,
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
    languageButtonPressed: { opacity: 0.7 },
    languageButtonHover: { opacity: 0.9 },
    languageIcon: {
      marginRight: 2,
    },
    languageCode: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.black,
    },
    profileContent: {
      alignItems: 'center',
      marginTop: 16,
    },
    avatarWrapper: {
      width: MENU_AVATAR_SIZE,
      height: MENU_AVATAR_SIZE,
      borderRadius: MENU_AVATAR_SIZE / 2,
      alignSelf: 'center',
      marginBottom: 8,
      overflow: 'hidden',
    },
    avatarWrapperPressed: { opacity: 0.85 },
    avatarWrapperHover: { opacity: 0.9 },
    displayNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      marginTop: 0,
    },
    displayNameSpacer: {
      width: 32,
      minWidth: 32,
    },
    displayNameCenter: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: 0,
    },
    userName: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.black,
      textAlign: 'center',
    },
    editIconButton: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
    },
    editIconButtonPressed: { opacity: 0.7 },
    editIconButtonHover: { opacity: 0.9 },
    nameInput: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.black,
      textAlign: 'center',
      width: '100%',
      maxWidth: 260,
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderWidth: 1,
      borderColor: theme.gray?.[200] ?? '#E2E8F0',
      borderRadius: 12,
      backgroundColor: theme.background?.card ?? MENU_CARD_BG_FALLBACK,
    },
    displayNameEditContainer: {
      alignItems: 'center',
      width: '100%',
      paddingVertical: 12,
      paddingHorizontal: 8,
    },
    displayNameHint: {
      fontSize: 12,
      color: theme.gray?.[500] ?? '#718096',
      marginTop: 6,
      alignSelf: 'center',
    },
    saveCancelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      marginTop: 14,
    },
    saveButton: {
      paddingVertical: 10,
      paddingHorizontal: 20,
      borderRadius: 10,
      backgroundColor: theme.primary ?? theme.accent ?? '#6366F1',
      minWidth: 88,
      alignItems: 'center',
    },
    saveButtonPressed: { opacity: 0.7 },
    saveButtonHover: { opacity: 0.9 },
    saveButtonText: {
      fontSize: 15,
      fontWeight: '600',
      color: '#FFFFFF',
    },
    cancelButtonInline: {
      paddingVertical: 10,
      paddingHorizontal: 20,
      borderRadius: 10,
      backgroundColor: theme.gray?.[200] ?? '#EDF2F7',
      minWidth: 88,
      alignItems: 'center',
    },
    cancelButtonInlinePressed: { opacity: 0.7 },
    cancelButtonInlineHover: { opacity: 0.9 },
    cancelButtonInlineText: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.gray?.[600] ?? '#718096',
    },
    userHandle: {
      fontSize: 13,
      color: theme.gray[500],
      textAlign: 'center',
      marginTop: 2,
    },
    impersonationStatusWrap: {
      width: '100%',
      maxWidth: 280,
      marginTop: 12,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 10,
      backgroundColor: (theme.error ?? '#dc2626') + '14',
      borderWidth: 1,
      borderColor: (theme.error ?? '#dc2626') + '44',
      alignItems: 'center',
    },
    impersonationStatusText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.error ?? '#dc2626',
      textAlign: 'center',
      marginBottom: 8,
    },
    impersonationExitBtn: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
      backgroundColor: theme.error ?? '#dc2626',
    },
    impersonationExitBtnPressed: { opacity: 0.85 },
    impersonationExitBtnText: {
      color: theme.white,
      fontSize: 14,
      fontWeight: '700',
    },
    // Guest-upgrade strip — same shape as the impersonation strip, brand-colored instead of error-red.
    guestUpgradeWrap: {
      width: '100%',
      maxWidth: 280,
      marginTop: 12,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 10,
      backgroundColor: (theme.primary ?? '#442698') + '14',
      borderWidth: 1,
      borderColor: (theme.primary ?? '#442698') + '44',
      alignItems: 'center',
    },
    guestUpgradeText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.primary ?? '#442698',
      textAlign: 'center',
      marginBottom: 8,
    },
    guestUpgradeBtn: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
      backgroundColor: theme.primary ?? '#442698',
    },
    guestUpgradeBtnPressed: { opacity: 0.85 },
    guestUpgradeBtnText: {
      color: theme.white,
      fontSize: 14,
      fontWeight: '700',
    },
    menuContent: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      backgroundColor: theme.background?.card ?? MENU_CARD_BG_FALLBACK,
    },
    signOutFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 24 + (insets?.bottom ?? 0),
      backgroundColor: theme.background?.card ?? MENU_CARD_BG_FALLBACK,
      borderTopWidth: 1,
      borderTopColor: theme.gray?.[200] ?? '#EDF2F7',
    },
    menuItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 14,
      paddingHorizontal: 0,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray?.[200] ?? '#EDF2F7',
    },
    menuItemPressed: { opacity: 0.7 },
    menuItemHover: { opacity: 0.9 },
    menuItemLocked: {
      opacity: 0.55,
    },
    menuItemSignOut: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.gray?.[100] ?? '#F7FAFC',
      paddingVertical: 14,
      borderRadius: 10,
    },
    menuItemSignOutPressed: { opacity: 0.7 },
    menuItemSignOutHover: { opacity: 0.9 },
    menuItemIcon: {
      fontSize: 18,
      marginRight: 12,
    },
    menuItemIconLeft: {
      marginRight: 12,
    },
    /** Coin icon in the profile balance pill (18px) */
    menuItemCoinIconSmall: {
      width: 18,
      height: 18,
    },
    /** Coin icon in the menu item row (22px) */
    menuItemCoinIcon: {
      width: 22,
      height: 22,
      marginRight: 12,
    },
    /** World Cup menu row (Lucide Trophy); same footprint as 22px icons */
    menuItemWorldCupIcon: {
      width: 22,
      height: 22,
      marginRight: 12,
    },
    menuItemText: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.black,
    },
    menuItemTextFlex: {
      flex: 1,
    },
    menuItemSoundStatusText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray?.[600] ?? '#4A5568',
      marginLeft: 8,
    },
    menuPrivacyChevron: {
      marginLeft: 8,
    },
    menuPrivacySubItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      paddingLeft: 34,
      paddingRight: 0,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray?.[200] ?? '#EDF2F7',
    },
    menuPrivacySubItemPressed: { opacity: 0.7 },
    menuPrivacySubItemHover: { opacity: 0.9 },
    menuPrivacySubItemText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.black,
    },
    menuPrivacySubItemDangerText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.error,
    },
    menuItemTextSignOut: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.gray[600],
    },
    dailyRewardButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 12,
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 20,
      backgroundColor: theme.background?.cardElevated ?? theme.gray?.[100] ?? '#F7FAFC',
      borderWidth: 1,
      borderColor: theme.accent,
      alignSelf: 'center',
    },
    dailyRewardButtonPressed: { opacity: 0.7 },
    dailyRewardButtonHover: { opacity: 0.9 },
    dailyRewardButtonText: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.black,
    },
    dailyRewardCoinIcon: {
      width: 18,
      height: 18,
    },
  });



